export const OB_KEYS = {
  EMAIL_VERIFIED:  'tnb_ob_email_verified',
  COMPANY_DONE:    'tnb_ob_company_done',
  INTRO_DONE:      'tnb_ob_intro_done',
  DOMAIN_VERIFIED: 'tnb_ob_domain_verified',
  GST_VERIFIED:    'tnb_ob_gst_verified',
  PAY_VERIFIED:    'tnb_ob_pay_verified',
} as const;

export const obSet  = (key: string) => localStorage.setItem(key, 'true');
export const obGet  = (key: string) => localStorage.getItem(key) === 'true';
export const obClear = (key: string) => localStorage.removeItem(key);

export const obClearAll = () => {
  Object.values(OB_KEYS).forEach(k => localStorage.removeItem(k));
  sessionStorage.removeItem('onboarding_basic_details_draft');
  sessionStorage.removeItem('tnb_signup_password');
  sessionStorage.removeItem('tnb_otp_sent_at');
  sessionStorage.removeItem('tnb_otp_target_email');
  [
    'onboarding_BasicDetail', 'onboarding_profile_detail',
    'onboarding_CompanyDetail', 'onboarding_LookingFor',
    'social_type', 'loginWithGoogle', 'loginWithLinkedin',
    'auth_token', 'email', 'full_name',
    'tnb_otp_email', 'onboarding_domain',
    'tnb_ob_domain_data', 'tnb_ob_gst_data',
    // Legacy OTP timer keys
    'email_otp_resend_target_time', 'email_otp_resend_target_email',
    'tnb_otp_sent_at', 'tnb_otp_target_email',
  ].forEach(k => localStorage.removeItem(k));
};

// Read the social provider stored for onboarding ('google' | 'linkedin' | null).
// Handles both JSON-encoded ('"google"') and plain ('google') values.
export const getSocialType = (): string | null => {
  const raw = localStorage.getItem('social_type');
  if (raw) {
    let stored: string | null = null;
    try {
      const parsed = JSON.parse(raw);
      stored = typeof parsed === 'string' ? parsed : null;
    } catch {
      stored = raw;
    }
    if (stored && stored !== 'email' && stored !== 'undefined' && stored !== 'null') {
      return stored;
    }
  }
  if (localStorage.getItem('loginWithGoogle')) return 'google';
  if (localStorage.getItem('loginWithLinkedin') || localStorage.getItem('loginWithLinkedIn')) return 'linkedin';
  return null;
};

// True when the current user signed up via a social provider (Google / LinkedIn).
// Social providers verify the email themselves, so the email OTP step is skipped for them.
export const isSocialLogin = (): boolean => {
  const socialType = getSocialType();
  return socialType === 'google' || socialType === 'linkedin';
};

// Clean email string from quotes and whitespace
export const cleanEmail = (rawEmail: string | null | undefined): string => {
  if (!rawEmail) return '';
  let str = String(rawEmail).trim();
  if ((str.startsWith('"') && str.endsWith('"')) || (str.startsWith("'") && str.endsWith("'"))) {
    try {
      const parsed = JSON.parse(str);
      if (typeof parsed === 'string') str = parsed;
    } catch {
      str = str.slice(1, -1);
    }
  }
  return str.trim().toLowerCase();
};

// Get email of the active logged in / onboarding user
export const getCurrentUserEmail = (): string => {
  const otpEmail = localStorage.getItem('tnb_otp_email');
  if (otpEmail) {
    const cleaned = cleanEmail(otpEmail);
    if (cleaned) return cleaned;
  }
  const loggedInEmail = localStorage.getItem('email');
  if (loggedInEmail) {
    const cleaned = cleanEmail(loggedInEmail);
    if (cleaned) return cleaned;
  }
  return '';
};

/** Store the one email currently waiting for OTP and discard its stale timer. */
export const setPendingOtpEmail = (targetEmail: string): string => {
  const email = cleanEmail(targetEmail);
  if (!email) return '';

  const previousPendingEmail = cleanEmail(localStorage.getItem('tnb_otp_email'));
  const resendEmail = cleanEmail(sessionStorage.getItem('tnb_otp_target_email') || localStorage.getItem('tnb_otp_target_email'));
  if (previousPendingEmail !== email || (resendEmail && resendEmail !== email)) {
    clearOtpTimerStorage();
  }

  localStorage.setItem('tnb_otp_email', email);
  return email;
};

// Check if a specific email has been OTP-verified in localStorage
export const isEmailOtpVerified = (targetEmail?: string): boolean => {
  if (isSocialLogin()) return true;

  const email = targetEmail ? cleanEmail(targetEmail) : getCurrentUserEmail();
  if (!email) {
    return obGet(OB_KEYS.EMAIL_VERIFIED);
  }

  // Check specific key for this email
  const specificKey = `tnb_email_verified_${email}`;
  if (localStorage.getItem(specificKey) === 'true') {
    return true;
  }

  // Check tnb_verified_emails JSON object in localStorage
  const verifiedMapStr = localStorage.getItem('tnb_verified_emails');
  if (verifiedMapStr) {
    try {
      const map: Record<string, boolean> = JSON.parse(verifiedMapStr);
      if (map && map[email] === true) {
        return true;
      }
    } catch {
      /* ignore parse error */
    }
  }

  return false;
};

// Set verification status for a specific email in localStorage
export const setEmailOtpVerified = (targetEmail: string, verified: boolean = true) => {
  const email = cleanEmail(targetEmail);
  if (!email) return;

  const specificKey = `tnb_email_verified_${email}`;
  
  let map: Record<string, boolean> = {};
  const verifiedMapStr = localStorage.getItem('tnb_verified_emails');
  if (verifiedMapStr) {
    try {
      map = JSON.parse(verifiedMapStr) || {};
    } catch {
      map = {};
    }
  }

  if (verified) {
    localStorage.setItem(specificKey, 'true');
    map[email] = true;
    localStorage.setItem('tnb_verified_emails', JSON.stringify(map));

    const currentEmail = getCurrentUserEmail();
    if (!currentEmail || currentEmail === email) {
      obSet(OB_KEYS.EMAIL_VERIFIED);
    }
  } else {
    localStorage.removeItem(specificKey);
    delete map[email];
    if (Object.keys(map).length > 0) {
      localStorage.setItem('tnb_verified_emails', JSON.stringify(map));
    } else {
      localStorage.removeItem('tnb_verified_emails');
    }
  }
};

/** Helper to set localStorage item only if it has actually changed and value is valid (non-null, non-undefined). */
export const safeSetItem = (key: string, value: any, logReason?: string): boolean => {
  if (value === undefined || value === null || value === 'undefined' || value === 'null') {
    return false;
  }
  const strVal = typeof value === 'string' ? value : JSON.stringify(value);
  const currentVal = localStorage.getItem(key);
  if (currentVal === strVal) {
    return false; // unchanged, skip write
  }
  if (logReason) {
    console.log(`[Storage] Set localStorage '${key}' (${logReason})`);
  }
  localStorage.setItem(key, strVal);
  return true;
};

/** Helper to set sessionStorage item only if changed and valid. */
export const safeSetSessionItem = (key: string, value: any, logReason?: string): boolean => {
  if (value === undefined || value === null || value === 'undefined' || value === 'null') {
    return false;
  }
  const strVal = typeof value === 'string' ? value : JSON.stringify(value);
  const currentVal = sessionStorage.getItem(key);
  if (currentVal === strVal) {
    return false; // unchanged
  }
  if (logReason) {
    console.log(`[Storage] Set sessionStorage '${key}' (${logReason})`);
  }
  sessionStorage.setItem(key, strVal);
  return true;
};

/** Save auth token based on Remember Me option */
export const saveAuthToken = (token: string, rememberMe: boolean = false) => {
  if (!token) return;
  if (rememberMe) {
    localStorage.setItem('token', token);
    localStorage.setItem('authToken', token);
    localStorage.setItem('auth_token', token);
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('auth_token');
  } else {
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('authToken', token);
    sessionStorage.setItem('auth_token', token);
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('auth_token');
  }
};

/** Get active auth token from localStorage or sessionStorage */
export const getAuthToken = (): string => {
  return localStorage.getItem('token') ||
         localStorage.getItem('authToken') ||
         localStorage.getItem('auth_token') ||
         sessionStorage.getItem('token') ||
         sessionStorage.getItem('authToken') ||
         sessionStorage.getItem('auth_token') ||
         '';
};

/** Clear auth token from both localStorage and sessionStorage */
export const clearAuthToken = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('authToken');
  localStorage.removeItem('auth_token');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('authToken');
  sessionStorage.removeItem('auth_token');
};

/* ── Signup Password Storage Helpers (sessionStorage exclusively) ───────────── */
export const saveSignupPasswordSession = (password: string) => {
  if (password) {
    sessionStorage.setItem('tnb_signup_password', password);
  }
};

export const getSignupPasswordSession = (): string => {
  return sessionStorage.getItem('tnb_signup_password') || '';
};

export const clearSignupPasswordSession = () => {
  sessionStorage.removeItem('tnb_signup_password');
};

/* ── OTP Resend Timer Storage Helpers (sessionStorage) ─────────────────────── */
export const getOtpTimerStorage = (targetEmail: string): number | null => {
  const cleanTarget = cleanEmail(targetEmail);
  if (!cleanTarget) return null;
  const storedEmail = cleanEmail(sessionStorage.getItem('tnb_otp_target_email') || localStorage.getItem('tnb_otp_target_email'));
  if (storedEmail && storedEmail === cleanTarget) {
    const ts = sessionStorage.getItem('tnb_otp_sent_at') || localStorage.getItem('tnb_otp_sent_at');
    if (ts) return Number(ts);
  }
  return null;
};

export const setOtpTimerStorage = (targetEmail: string, timestamp: number) => {
  const cleanTarget = cleanEmail(targetEmail);
  if (!cleanTarget) return;
  sessionStorage.setItem('tnb_otp_sent_at', String(timestamp));
  sessionStorage.setItem('tnb_otp_target_email', cleanTarget);
  localStorage.setItem('tnb_otp_sent_at', String(timestamp));
  localStorage.setItem('tnb_otp_target_email', cleanTarget);
};

export const clearOtpTimerStorage = () => {
  sessionStorage.removeItem('tnb_otp_sent_at');
  sessionStorage.removeItem('tnb_otp_target_email');
  localStorage.removeItem('tnb_otp_sent_at');
  localStorage.removeItem('tnb_otp_target_email');
};

/* ── Basic Details Storage Helpers (localStorage, strictly NO passwords) ───── */
export const CANONICAL_BASIC_DETAILS_KEY = 'onboarding_BasicDetail';

export interface StoredBasicDetails {
  legalName: string;
  officialEmail: string;
  yourDesignation: string;
  yourDesignationId: number;
  termsAccepted?: boolean;
}

export const saveEncryptedBasicDetails = async (details: StoredBasicDetails & { password?: string }): Promise<void> => {
  try {
    // If a password was provided, store it strictly in sessionStorage
    if (details.password) {
      saveSignupPasswordSession(details.password);
    }

    const { encrypt } = await import('customhooks/helper');
    // Save ONLY basic non-sensitive details to localStorage (strip password)
    const cleanDetails: StoredBasicDetails = {
      legalName: details.legalName || '',
      officialEmail: details.officialEmail || '',
      yourDesignation: details.yourDesignation || '',
      yourDesignationId: details.yourDesignationId || 0,
      termsAccepted: details.termsAccepted ?? true,
    };

    const jsonStr = JSON.stringify(cleanDetails);
    const encryptedPayload = await encrypt(jsonStr);

    localStorage.setItem(CANONICAL_BASIC_DETAILS_KEY, encryptedPayload);
    localStorage.removeItem('onboarding_BasicDetail_encrypted');
  } catch (err) {
    console.error('Failed to save encrypted basic details:', err);
  }
};

export const loadEncryptedBasicDetails = async (): Promise<StoredBasicDetails | null> => {
  try {
    const encryptedStr = localStorage.getItem(CANONICAL_BASIC_DETAILS_KEY) || localStorage.getItem('onboarding_BasicDetail_encrypted');
    if (!encryptedStr) return null;

    const { decrypt } = await import('customhooks/helper');
    let decryptedText = '';
    if (encryptedStr.trim().startsWith('{')) {
      decryptedText = encryptedStr;
    } else {
      decryptedText = await decrypt(encryptedStr);
    }

    if (!decryptedText) return null;
    let parsed: any;
    try {
      parsed = JSON.parse(decryptedText);
    } catch {
      return null;
    }

    const basicObj = parsed['Basic Details'] || parsed.basic_details || parsed;
    const legalName = basicObj.legalName || basicObj.full_name || basicObj.name || '';
    const officialEmail = basicObj.officialEmail || basicObj.email || '';
    const yourDesignation = basicObj.yourDesignation || basicObj.designation_name || basicObj.designation || '';
    const yourDesignationId = basicObj.yourDesignationId || basicObj.designation_id || 0;

    return {
      legalName,
      officialEmail,
      yourDesignation,
      yourDesignationId,
      termsAccepted: basicObj.termsAccepted ?? true,
    };
  } catch (err) {
    console.error('Failed to load basic details:', err);
    return null;
  }
};




